import { FC, Fragment, useCallback, useMemo, useState } from "react";
import { LinearProgress, Stack } from "@mui/material";
import { LiveUsageResourceGridToolbar } from "./toolbar/LiveUsageResourceGridToolbar";
import { ResourcesMasterGrid } from "../../../common/resources-ag-grid/ResourcesMasterGrid";
import type { GridApi } from "ag-grid-community";
import { ResourceType } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { liveUsageMgmtAccountIdsSelector } from "@/store/live-usage-mgmt/selectors/store-selectors/liveUsageMgmtAccountIdsSelector";
import { gridResourceDataLoadingSelector } from "@/store/resources-grid/selectors/resource-data/gridResourceDataLoadingSelector";
import { liveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector } from "@/store/live-usage-mgmt/selectors/filters/liveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector";
import { RootState } from "@/store/store";
import { LiveUsageResourceSingleHeader } from "@/components/pages/live-usage-mgmt/components/resources-area/LiveUsageResourceSingleHeader";
import { liveUsageResourceMetadataTotalResourcesCountSelector } from "@/store/live-usage-mgmt/selectors/resource-type-data/resource-metadata/count/liveUsageResourceMetadataTotalResourcesCountSelector";
import { liveUsageMgmtRegionsSelector } from "@/store/live-usage-mgmt/selectors/store-selectors/liveUsageMgmtRegionsSelector";
import { ResourcesGridResourcePayloadType } from "@/store/resources-grid/utils/types/common";
import { liveUsageResourceTypeFailedForAllAccountsSelector } from "@/store/live-usage-mgmt/selectors/resource-info-status/liveUsageResourceTypeFailedForAllAccountsSelector";
import { LiveUsageResourceInfoStatusForSingleAccount } from "@/components/pages/live-usage-mgmt/components/resources-area/resource-info/LiveUsageResourceInfoStatusForSingleAccount";

interface LiveUsageResourceGridProps {
  resourceType: ResourceType;
  allResourcesView?: boolean;
  masterGridId: string;
}

export const LiveUsageResourceGrid: FC<LiveUsageResourceGridProps> = ({
  resourceType,
  allResourcesView,
  masterGridId,
}) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const accountIds = useAppSelector(liveUsageMgmtAccountIdsSelector);
  const loading = useAppSelector((state) =>
    gridResourceDataLoadingSelector(state, masterGridId),
  );

  const resourcesTotalCount = useAppSelector((state) =>
    liveUsageResourceMetadataTotalResourcesCountSelector(state, resourceType),
  );

  const isAllMissing = useAppSelector((state) =>
    liveUsageResourceTypeFailedForAllAccountsSelector(state, resourceType),
  );

  const regions = useAppSelector(liveUsageMgmtRegionsSelector);

  const resourceFiltersSelector = useCallback(
    (state: RootState) => {
      return liveUsageMgmtAppliedFiltersWithRegionsByResourceTypeSelector(
        state,
        resourceType,
      );
    },
    [resourceType],
  );

  const resourceFilters = useAppSelector(resourceFiltersSelector);

  const dataToFetchResources: ResourcesGridResourcePayloadType = useMemo(() => {
    return {
      resourceFilters,
      accountIds: accountIds ?? [],
      regions: regions ?? undefined,
    };
  }, [accountIds, resourceFilters, regions]);

  return (
    <Stack height="100%" key={resourceType}>
      {loading && <LinearProgress />}

      {!allResourcesView && (
        <LiveUsageResourceSingleHeader
          count={resourcesTotalCount}
          resourceType={resourceType}
        />
      )}

      {isAllMissing ? (
        <LiveUsageResourceInfoStatusForSingleAccount
          resourceType={resourceType}
        />
      ) : (
        <Fragment>
          <LiveUsageResourceGridToolbar
            gridApi={gridApi}
            masterResourceType={resourceType}
            masterGridId={masterGridId}
          />
          <ResourcesMasterGrid
            gridApi={gridApi}
            dataToFetchResources={dataToFetchResources}
            notFullHeight={allResourcesView}
            resourceType={resourceType}
            masterResourceType={resourceType}
            masterGridId={masterGridId}
            setGridApi={setGridApi}
          />
        </Fragment>
      )}
    </Stack>
  );
};
